import * as React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

import { cn } from '@/lib/utils';

type ScrollbarSize = 'default' | 'lg';

/**
 * Thumb-only sizing; track width stays the same as default.
 * Radix sets thumb height via --radix-scroll-area-thumb-height, so min-height alone
 * does not apply; use max() with that variable for a visible floor on long lists.
 */
const scrollbarThumbClasses: Record<ScrollbarSize, string> = {
  default: '',
  lg: 'h-[max(var(--radix-scroll-area-thumb-height),max(3rem,22%))]',
};

type ScrollAreaProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
  scrollbarSize?: ScrollbarSize;
  viewportClassName?: string;
};

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(({ className, children, scrollbarSize = 'default', viewportClassName, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    className={cn('relative overflow-hidden', className)}
    {...props}
  >
    {/*
      Radix sizes the viewport's inner wrapper with display:table, which grows to
      max-content around a long unbroken string (an S3 URL, a filename) instead of
      wrapping. Nothing renders a horizontal ScrollBar, so that extra width is
      unreachable and reads as clipped content. Force the wrapper back to a normal
      shrinkable block. Radix sets display and min-width inline, so those two need
      the bang to win; a caller overriding them via viewportClassName needs one too.
    */}
    <ScrollAreaPrimitive.Viewport
      data-scroll-viewport
      className={cn(
        'h-full w-full rounded-[inherit]',
        '[&>div]:!block [&>div]:!w-full [&>div]:!min-w-0 [&>div]:max-w-full',
        viewportClassName,
      )}
    >
      {children}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar size={scrollbarSize} />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

type ScrollBarProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar> & {
  size?: ScrollbarSize;
};

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  ScrollBarProps
>(({ className, orientation = 'vertical', size = 'default', ...props }, ref) => (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      ref={ref}
      orientation={orientation}
      className={cn(
        'flex touch-none select-none transition-colors',
        orientation === 'vertical' &&
          'h-full w-2.5 border-l border-l-transparent p-[1px]',
        orientation === 'horizontal' &&
          'h-2.5 flex-col border-t border-t-transparent p-[1px]',
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        className={cn('relative flex-1 rounded-full bg-border', scrollbarThumbClasses[size])}
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
export type { ScrollAreaProps, ScrollbarSize };
